import Alert from '@mui/material/Alert';
import { BigNumber, utils } from 'ethers';
import React, { useState, useContext, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useAccount, useBlockNumber } from 'wagmi';
import { ContractContext } from '../../../context-providers/ContractsProvider';
import { ServicesContext } from '../../../context-providers/ServicesProvider';
import { contractAddresses, tokenDecimals } from '../../../helpers/config/contracts.config';
import { hardcodedErrorCodes } from '../../../helpers/error-message.helper';
import { balanceStore } from '../../../stores/BalanceStore';
import AmountInput from '../../AmountInput/AmountInput';
import NereusButton from '../../NereusButton/NereusButton';
import StatusIcon from '../../StatusIcon/StatusIcon';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import './DepositDialog.scss';

const buttonTexts = {
    approveAndDeposit: 'Approve and Deposit USDC',
    enterAmount: 'Enter Amount',
    insufficientFunds: 'Insufficient Funds',
    approving: 'Approving USDC',
    deposit: 'Deposit USDC',
    depositing: 'Depositing USDC',
    retryApproveAndDeposit: 'Retry Approve and Deposit USDC',
    retryDeposit: 'Retry Deposit USDC'
};

const DepositDialog: React.FC = observer(() => {
    const { data: blockNumber } = useBlockNumber({ watch: true });
    const { address: userAddress } = useAccount();
    const { depositWithdrawService } =  useContext(ServicesContext);
    const { usdcContract } = useContext(ContractContext);
    const [amount, setAmount] = useState<string>('0');
    const [error, setError] = useState<string | null>(null);
    const [btnDisabled, setBtnDisabled] = useState<boolean>(true);
    const [approvedAmount, setApprovedAmount] = useState<BigNumber>(BigNumber.from('0'));
    const [buttonText, setButtonText] = useState<string>(buttonTexts.enterAmount);
    const [isPending, setIsPending] = useState<boolean>(false);
    const [isRetry, setIsRetry] = useState<boolean>(false);

    useEffect(() => {
        const getAndSetApprovedAmount = async () => {
            const allowed = await usdcContract.read.allowance([userAddress, contractAddresses.base.ddp]);

            setApprovedAmount(BigNumber.from(allowed));
        }
        if (userAddress && blockNumber && usdcContract) {
            getAndSetApprovedAmount();
        } else {
            setApprovedAmount(BigNumber.from('0'));
        }

    }, [userAddress, blockNumber, usdcContract]);

    useEffect(() => {
        if (!amount || Number(amount) === 0) {
            return setButtonText(buttonTexts.enterAmount);
        } else {
            if (error) {
                if (error === hardcodedErrorCodes.FE_NOT_ENOUGH_WALLET_BALANCE) {
                    return setButtonText(buttonTexts.insufficientFunds);
                }
            }
            if (approvedAmount.gte(utils.parseUnits(amount, tokenDecimals.usdc))) {
                if (isPending) {
                    return setButtonText(buttonTexts.depositing);
                } else {
                    return setButtonText(isRetry ? buttonTexts.retryDeposit : buttonTexts.deposit);
                }

            } else {
                if (isPending) {
                    return setButtonText(buttonTexts.approving);
                } else {
                    return setButtonText(isRetry ? buttonTexts.retryApproveAndDeposit : buttonTexts.approveAndDeposit);
                }
            }
        }

    }, [amount, error, approvedAmount, isRetry, isPending]);

    useEffect(() => {
        if ((amount && Number(amount) > 0) && (error !== hardcodedErrorCodes.FE_NOT_ENOUGH_WALLET_BALANCE)) {
            setBtnDisabled(false);
        } else {
            setBtnDisabled(true);
        }
    }, [amount, error]);

    useEffect(() => {
        if (amount && Number(amount) > Number(balanceStore.usdcTokenBalanceDecimal)) {
            setError(hardcodedErrorCodes.FE_NOT_ENOUGH_WALLET_BALANCE);
        } else {
            setError(null);
        }
    }, [amount, balanceStore.usdcTokenBalanceDecimal]);

    useEffect(() => {
        if (userAddress && blockNumber !== undefined) {
            balanceStore.fetchTokenBalance(usdcContract, userAddress, Number(blockNumber));
        }
    }, [blockNumber, usdcContract, userAddress]);

    const handleApproveAndDeposit = async () => {
        setIsPending(true);
        setIsRetry(false);
        if (approvedAmount.lt(utils.parseUnits(amount, tokenDecimals.usdc))) {
            try {
                setButtonText(buttonTexts.approving);
                await depositWithdrawService.approveMaxAmount();
            } catch (err) {
                setIsPending(false);
                setIsRetry(true);
                return;
            }
        }

        try {
            setButtonText(buttonTexts.depositing);
            await depositWithdrawService.deposit(utils.parseUnits(amount, tokenDecimals.usdc));
        } catch (err) {
            setButtonText(buttonTexts.retryDeposit);
            setIsPending(false);
            setIsRetry(true);
            return;
        }
        setIsPending(false);
    };

    return (
        <ModalWrapper className="DepositDialog"
            headerChildren={<h2 className="nereus-body-m">Deposit</h2>}
            bodyChildren={<div className="DepositDialog__body">
                <div>
                    <AmountInput label="Pay" asset="USDC" hasMaxButton={true} value={amount}
                                 available={balanceStore.usdcTokenBalanceDecimal}
                                 onChange={amount => setAmount(amount + '')}/>
                    {error && <Alert className="DepositDialog__error mt-4" variant="standard" severity="error">
                        <span className="nereus-caption">{error}</span>
                    </Alert>}
                </div>

                <div className="flex pb-4 mt-4">
                    <NereusButton disabled={btnDisabled || isPending} variant="contained"
                                  onClick={handleApproveAndDeposit} className="bg-light-green text-dark-blue w-full">
                        {buttonText} {isPending && <StatusIcon className="ml-1 inline" status="disabled-pending"/>}
                    </NereusButton>
                </div>
            </div>}
        />
    );
});

export default DepositDialog;
